import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Filter, Download, RefreshCw, ChevronDown, Package, CheckCircle2,
  Clock, AlertTriangle, IndianRupee, TrendingUp, TrendingDown,
  FileText, FileSpreadsheet, FileType2, BarChart3, LineChart as LineIcon,
  Users, MapPin, PhoneOff, FilePlus, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import { reportsSummary, exportOrders, listActivityLogs } from "@/lib/api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { ROLE_LABELS, fmtDateTime } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// === Date utils (mirrors AllOrders) ===
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const lastMonthRange = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const end = new Date(d.getFullYear(), d.getMonth(), 0);
  const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return { from: fmt(start), to: fmt(end) };
};
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const computePreset = (fromD, toD) => {
  const t = todayISO();
  if (fromD === t && toD === t) return "today";
  if (fromD === daysAgoISO(7) && toD === t) return "7d";
  if (fromD === monthStartISO() && toD === t) return "month";
  const lm = lastMonthRange();
  if (fromD === lm.from && toD === lm.to) return "lastmonth";
  return "custom";
};
const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "month", label: "Current Month" },
  { key: "lastmonth", label: "Last Month" },
  { key: "custom", label: "Custom Range" },
];

// === Indian numbering currency formatter ===
const fmtINR = (n) => {
  if (n == null || isNaN(n)) return "₹0";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};
const fmtINRdetail = (n) => {
  if (n == null || isNaN(n)) return "₹0";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// === Status display ===
const STATUS_BAR = [
  { key: "draft", label: "Draft", color: "#9aa0ad" },
  { key: "new_order", label: "New", color: "#3b82f6" },
  { key: "ordered", label: "Ordered", color: "#f97316" },
  { key: "pending_pickup", label: "Pending Pickup", color: "#10b981" },
  { key: "delayed", label: "Delayed", color: "#e11d48" },
  { key: "invoiced", label: "Completed", color: "#15803d" },
  { key: "returned_to_stock", label: "Returned", color: "#a855f7" },
  { key: "unclaimed", label: "Unclaimed", color: "#4b4d57" },
];

// Module-level column definitions — hoisted so React doesn't get a fresh array
// reference on every parent render (which would force PerfTable to re-evaluate
// all rows even when the data hasn't changed). Renderers depend only on the
// per-row arg and the module-scoped formatters above.
const BRANCH_PERF_COLUMNS = [
  { key: "loc", label: "Location", render: (r) => (
    <div>
      <div className="font-semibold text-[#1a1b22]">{r.location_code}</div>
      <div className="text-[10px] text-[#747684]">{r.location_name}</div>
    </div>
  ) },
  { key: "total", label: "Total" },
  { key: "completed", label: "Completed", cls: "text-emerald-700 font-semibold" },
  { key: "pending", label: "Pending", cls: "text-amber-700 font-semibold" },
  { key: "delayed", label: "Delayed", cls: "text-rose-700 font-semibold" },
  { key: "completion_pct", label: "Completion %", render: (r) => (
    <span className={`${r.completion_pct >= 70 ? "text-emerald-700" : r.completion_pct >= 40 ? "text-amber-700" : "text-rose-700"} font-semibold`}>{r.completion_pct}%</span>
  ) },
  { key: "billing", label: "Billing", render: (r) => fmtINRdetail(r.billing) },
  { key: "avg_lead", label: "Avg Lead", render: (r) => `${r.avg_lead}d` },
];

const USER_PERF_COLUMNS = [
  { key: "user", label: "User", render: (r) => <span className="font-semibold text-[#1a1b22]">{r.user}</span> },
  { key: "orders", label: "Orders Created" },
  { key: "completed", label: "Completed", cls: "text-emerald-700 font-semibold" },
  { key: "pending", label: "Pending", cls: "text-amber-700 font-semibold" },
  { key: "billing", label: "Billing", render: (r) => fmtINRdetail(r.billing) },
  { key: "avg_lead", label: "Avg Lead", render: (r) => `${r.avg_lead}d` },
];

const Reports = () => {
  const { currentUser, currentLocation, locations, users } = useApp();
  const navigate = useNavigate();

  // Filters
  const [filterFromDate, setFilterFromDate] = useState(monthStartISO());
  const [filterToDate, setFilterToDate] = useState(todayISO());
  const [datePreset, setDatePreset] = useState("month");
  const [filterLocation, setFilterLocation] = useState("ALL");
  const [filterCreatedBy, setFilterCreatedBy] = useState("ALL");
  const [filterOrderType, setFilterOrderType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterModel, setFilterModel] = useState("");
  // The applied filters drive fetching. Apply button or auto-on-change keeps things simple — auto for snappier UX.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);

  const effectiveLocation = filterLocation !== "ALL" ? filterLocation : currentLocation;

  const buildParams = () => {
    const params = {};
    if (effectiveLocation && effectiveLocation !== "ALL") params.location_code = effectiveLocation;
    if (filterCreatedBy !== "ALL") params.created_by = filterCreatedBy;
    if (filterOrderType !== "ALL") params.order_against = filterOrderType;
    if (filterStatus !== "ALL") params.status = filterStatus;
    if (filterModel) params.model_name = filterModel;
    if (filterFromDate) params.from_date = filterFromDate;
    if (filterToDate) params.to_date = filterToDate;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const r = await reportsSummary(params);
      setData(r);
      // Recent activity respects the same date + location filters
      const actParams = { limit: 12 };
      if (params.location_code) actParams.location_code = params.location_code;
      if (params.from_date) actParams.from_date = params.from_date;
      if (params.to_date) actParams.to_date = params.to_date;
      if (filterCreatedBy && filterCreatedBy !== "ALL") actParams.actor = filterCreatedBy;
      try {
        const act = await listActivityLogs(actParams);
        setRecentActivity(act?.logs || []);
      } catch {
        setRecentActivity([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [currentLocation, filterLocation, filterCreatedBy, filterOrderType, filterStatus, filterModel, filterFromDate, filterToDate]);

  useEffect(() => {
    setDatePreset(computePreset(filterFromDate, filterToDate));
  }, [filterFromDate, filterToDate]);

  const applyPreset = (key) => {
    setDatePreset(key);
    const t = todayISO();
    if (key === "today") { setFilterFromDate(t); setFilterToDate(t); }
    else if (key === "7d") { setFilterFromDate(daysAgoISO(7)); setFilterToDate(t); }
    else if (key === "month") { setFilterFromDate(monthStartISO()); setFilterToDate(t); }
    else if (key === "lastmonth") {
      const { from, to } = lastMonthRange();
      setFilterFromDate(from); setFilterToDate(to);
    }
  };

  const reset = () => {
    setFilterLocation("ALL");
    setFilterCreatedBy("ALL");
    setFilterOrderType("ALL");
    setFilterStatus("ALL");
    setFilterModel("");
    setFilterFromDate(monthStartISO());
    setFilterToDate(todayISO());
  };

  const handleExport = async (format) => {
    setExportOpen(false);
    try {
      const params = { format, include_drafts: true, view: "active", ...buildParams() };
      const resp = await exportOrders(params);
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `IVA_REPORT.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format === "pdf_summary" ? "PDF Summary" : format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const goAllOrders = (q) => navigate(`/orders${q ? `?${q}` : ""}`);

  // Derived chart data
  const statusChartData = useMemo(() => {
    if (!data?.by_status) return [];
    return STATUS_BAR
      .map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        count: data.by_status[s.key] || 0,
      }))
      .filter((x) => x.count > 0);
  }, [data]);

  const orderTrend = data?.order_trend || [];
  const billingTrend = data?.billing_trend_data || [];

  return (
    <div className="space-y-4 pb-10" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-semibold text-yamaha-800 leading-tight tracking-tight">Reports & MIS</h1>
          <p className="text-[13px] text-[#444653] mt-0.5">
            Operational insights, billing visibility and downloadable MIS reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can(currentUser, "reports_export") && (
          <DropdownMenu open={exportOpen} onOpenChange={setExportOpen}>
            <DropdownMenuTrigger
              data-testid="export-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold"
            >
              <Download className="w-3.5 h-3.5" />
              Export
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid="export-xlsx">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-2" />Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv">
                <FileText className="w-3.5 h-3.5 mr-2" />CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport("pdf_summary")} data-testid="export-pdf-summary">
                <FileType2 className="w-3.5 h-3.5 mr-2" />PDF Summary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf-detailed">
                <FileType2 className="w-3.5 h-3.5 mr-2" />PDF Detailed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-0 z-10 bg-white rounded-xl border border-[#e2e1eb] shadow-sm" data-testid="filter-bar">
        <div className="px-4 py-3 bg-[#faf8ff] rounded-xl space-y-2.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-1.5 text-[#747684]">
              <Filter className="w-3.5 h-3.5" />
              <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
            </div>
            <select
              data-testid="filter-location"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
            >
              <option value="ALL">All Locations</option>
              {locations.map((l) => <option key={l.code} value={l.code}>{l.code} — {l.name}</option>)}
            </select>
            <select
              data-testid="filter-created-by"
              value={filterCreatedBy}
              onChange={(e) => setFilterCreatedBy(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
            >
              <option value="ALL">All Users</option>
              {users.map((u) => <option key={u.id || u.name} value={u.name}>{u.name}</option>)}
            </select>
            <select
              data-testid="filter-order-type"
              value={filterOrderType}
              onChange={(e) => setFilterOrderType(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
            >
              <option value="ALL">All Order Types</option>
              <option value="job_card">Job Card</option>
              <option value="counter_sale">Counter Sale</option>
            </select>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input !w-auto !py-1.5 !text-[12px]"
            >
              <option value="ALL">All Status</option>
              <option value="draft">Draft</option>
              <option value="new_order">New</option>
              <option value="ordered">Ordered</option>
              <option value="pending_pickup">Pending Pickup</option>
              <option value="invoiced">Completed</option>
              <option value="returned_to_stock">Returned</option>
              <option value="unclaimed">Unclaimed</option>
            </select>
            <input
              data-testid="filter-model"
              placeholder="Vehicle Model"
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              className="input !w-40 !py-1.5 !text-[12px]"
            />
            <button
              data-testid="apply-filters"
              onClick={load}
              className="px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold"
            >
              Apply Filters
            </button>
            <button
              data-testid="reset-filters"
              onClick={reset}
              className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] font-semibold text-[#444653] hover:bg-white"
            >
              Reset
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#747684]">Date Range:</span>
            <div className="flex items-center gap-1" data-testid="date-presets">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`preset-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                    datePreset === p.key
                      ? "bg-yamaha-700 text-white border-yamaha-700"
                      : "bg-white text-[#444653] border-[#e2e1eb] hover:border-yamaha-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 ml-1">
              <input
                data-testid="filter-from-date"
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                className="input !w-36 !py-1 !text-[11px]"
              />
              <span className="text-[#747684] text-[11px]">to</span>
              <input
                data-testid="filter-to-date"
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                className="input !w-36 !py-1 !text-[11px]"
              />
            </div>
          </div>
        </div>
      </div>

      {!data ? (
        <div className="p-12 text-center text-[#747684]">Loading reports…</div>
      ) : (
        <>
          {/* KPI Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
            <KPICard testid="kpi-total" icon={Package} label="Total Orders" value={data.total_orders} sub="Orders created in period" onClick={() => goAllOrders()} />
            <KPICard testid="kpi-completed" icon={CheckCircle2} label="Completed Orders" value={data.invoiced} sub="Invoiced / released" accent="emerald" onClick={() => goAllOrders()} />
            <KPICard testid="kpi-pending" icon={Clock} label="Pending Orders" value={data.pending} sub="New + Ordered + Pickup" accent="amber" onClick={() => goAllOrders()} />
            <KPICard testid="kpi-delayed" icon={AlertTriangle} label="Delayed Orders" value={data.delayed} sub="EDD passed" accent="rose" onClick={() => goAllOrders()} />
            <KPICard testid="kpi-billing" icon={IndianRupee} label="Total Billing Amount" value={fmtINR(data.total_billing)} sub="Sum of invoiced bills" accent="emerald-strong" />
            <KPICard testid="kpi-aov" icon={Wallet} label="Average Order Value" value={fmtINR(data.avg_order_value)} sub="Billing ÷ Completed" accent="blue" />
            <KPICard testid="kpi-lead" icon={Clock} label="Average Lead Time" value={`${data.avg_lead_time_days}d`} sub="Pickup − Created" accent="indigo" />
            <BillingTrendCard testid="kpi-trend" pct={data.billing_trend_pct} />
            <KPICard testid="kpi-drafts" icon={FilePlus} label="Draft Orders" value={data.drafts} sub="Saved but not submitted" highlight="amber" onClick={() => goAllOrders()} />
            <KPICard testid="kpi-unclaimed" icon={PhoneOff} label="Unclaimed Orders" value={data.unclaimed} sub="Customer did not collect" highlight="purple" onClick={() => goAllOrders()} />
          </div>

          {/* Section 1: Orders by Status */}
          <ChartCard title="Orders by Status" icon={BarChart3} testid="section-status">
            {statusChartData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statusChartData} layout="vertical" margin={{ left: 60, right: 30 }}>
                  <CartesianGrid stroke="#f1f0f7" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(v) => [v, "Orders"]} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {statusChartData.map((d) => <Cell key={d.key} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Section 2 + 3: Order trend & Billing trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="Order Trend" icon={LineIcon} testid="section-order-trend">
              {orderTrend.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={orderTrend}>
                    <CartesianGrid stroke="#f1f0f7" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => [v, "Orders"]} />
                    <Line type="monotone" dataKey="count" stroke="#003399" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
            <ChartCard title="Billing Trend" icon={IndianRupee} testid="section-billing-trend">
              {billingTrend.length === 0 ? (
                <EmptyChart label="No billing data in selected period" />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={billingTrend}>
                    <CartesianGrid stroke="#f1f0f7" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtINR(v).replace("₹", "")} />
                    <Tooltip formatter={(v) => [fmtINR(v), "Billing"]} />
                    <Line type="monotone" dataKey="amount" stroke="#15803d" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Section 5: Branch Performance */}
          <PerfTable
            title="Branch Performance"
            icon={MapPin}
            testid="section-branch"
            rows={data.branch_performance}
            columns={BRANCH_PERF_COLUMNS}
            emptyText="No branch data for selected filters."
          />

          {/* Section 6: User Performance */}
          <PerfTable
            title="User Performance"
            icon={Users}
            testid="section-user"
            rows={data.user_performance}
            columns={USER_PERF_COLUMNS}
            emptyText="No user activity in selected period."
          />

          {/* Section 7: Recent User Activity (audit feed) */}
          <RecentActivityFeed entries={recentActivity} />
        </>
      )}
    </div>
  );
};

// ========= Small components =========
const ACCENT_TO_CLS = {
  emerald: "text-emerald-700",
  "emerald-strong": "text-green-800",
  amber: "text-amber-700",
  rose: "text-rose-700",
  blue: "text-blue-700",
  indigo: "text-indigo-700",
  purple: "text-purple-700",
};

const KPICard = ({ testid, icon: Icon, label, value, sub, accent, highlight, onClick }) => {
  const isHighlight = highlight === "amber";
  const isPurple = highlight === "purple";
  const valCls = isHighlight ? "text-amber-700" : isPurple ? "text-purple-700" : ACCENT_TO_CLS[accent] || "text-[#1a1b22]";
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`bg-white rounded-xl border p-3.5 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
        isHighlight ? "border-amber-300 bg-amber-50" : isPurple ? "border-purple-300 bg-purple-50" : "border-[#e2e1eb]"
      }`}
      style={{ minHeight: 100 }}
    >
      <div className="flex items-start justify-between">
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
          isHighlight ? "bg-amber-100 text-amber-700" : isPurple ? "bg-purple-100 text-purple-700" : "bg-yamaha-50 text-yamaha-700"
        }`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className={`mt-2 text-[24px] font-semibold leading-none ${valCls}`}>{value}</div>
      <div className="text-[11.5px] font-semibold text-[#444653] mt-1.5">{label}</div>
      {sub && <div className="text-[10px] text-[#747684] mt-0.5">{sub}</div>}
    </div>
  );
};

const BillingTrendCard = ({ testid, pct }) => {
  if (pct === null || pct === undefined) {
    return (
      <div data-testid={testid} className="bg-white rounded-xl border border-[#e2e1eb] p-3.5" style={{ minHeight: 100 }}>
        <div className="w-8 h-8 rounded-md flex items-center justify-center bg-yamaha-50 text-yamaha-700">
          <TrendingUp className="w-4 h-4" />
        </div>
        <div className="mt-2 text-[24px] font-semibold leading-none text-[#747684]">—</div>
        <div className="text-[11.5px] font-semibold text-[#444653] mt-1.5">Billing Trend</div>
        <div className="text-[10px] text-[#747684] mt-0.5">No comparable previous period</div>
      </div>
    );
  }
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-[#e2e1eb] p-3.5" style={{ minHeight: 100 }}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`mt-2 text-[24px] font-semibold leading-none ${positive ? "text-emerald-700" : "text-rose-700"}`}>
        {positive ? "+" : ""}{pct}%
      </div>
      <div className="text-[11.5px] font-semibold text-[#444653] mt-1.5">Billing Trend</div>
      <div className="text-[10px] text-[#747684] mt-0.5">vs previous period</div>
    </div>
  );
};

const ChartCard = ({ title, icon: Icon, testid, children }) => (
  <div className="bg-white rounded-xl border border-[#e2e1eb] p-4" data-testid={testid}>
    <h3 className="text-[13px] font-semibold mb-3 flex items-center gap-2 text-[#1a1b22]">
      {Icon ? <Icon className="w-4 h-4 text-yamaha-700" /> : null}
      {title}
    </h3>
    {children}
  </div>
);

const EmptyChart = ({ label = "No data for selected filters" }) => (
  <div className="h-60 flex items-center justify-center text-[#747684] text-[12px]">{label}</div>
);

const PerfTable = ({ title, icon: Icon, testid, rows, columns, emptyText }) => {
  const [sortField, setSortField] = useState(columns[1]?.key || columns[0].key);
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [rows, sortField, sortDir]);

  return (
    <div className="bg-white rounded-xl border border-[#e2e1eb]" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#e2e1eb] flex items-center gap-2">
        {Icon ? <Icon className="w-4 h-4 text-yamaha-700" /> : null}
        <h3 className="text-[13px] font-semibold text-[#1a1b22]">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        {(!sorted || sorted.length === 0) ? (
          <div className="px-4 py-8 text-center text-[#747684] text-[12px]">{emptyText}</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#fafbff] border-b border-[#e2e1eb] text-[10px] uppercase tracking-wider text-[#747684]">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="px-4 py-2 text-left font-bold cursor-pointer hover:text-yamaha-700"
                    onClick={() => {
                      if (sortField === c.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else { setSortField(c.key); setSortDir("desc"); }
                    }}
                    data-testid={`perf-th-${c.key}`}
                  >
                    {c.label}
                    {sortField === c.key && <span className="ml-1 text-yamaha-700">{sortDir === "asc" ? "↑" : "↓"}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, idx) => (
                <tr
                  key={r.user || r.branch || r.location || r.month || `row-${idx}`}
                  className="border-b border-[#f1f0f7] hover:bg-[#faf8ff]"
                  data-testid={`perf-row-${idx}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-2 ${c.cls || "text-[#444653]"}`}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Reports;

// ========= Recent Activity Feed (audit) =========
const ACTION_DISPLAY = {
  ORDER_CREATED: "Created Order",
  DRAFT_SAVED: "Saved Draft",
  DRAFT_SUBMITTED: "Submitted Draft",
  ORDER_UPDATED: "Updated Order",
  MARKED_ORDERED: "Marked Ordered",
  RECEIVED_PARTS: "Received Parts",
  PARTIAL_RECEIPT: "Partially Received",
  BACKORDERED: "Backordered",
  EDD_UPDATED: "Updated EDD",
  ORDER_COMPLETED: "Completed",
  REMINDER_SENT: "Sent Reminder",
  CNR_LOGGED: "Log Follow-up",
  UNCLAIMED: "Marked Unclaimed",
  RETURNED_TO_STOCK: "Returned To Stock",
  ORDER_CANCELLED: "Cancelled Order",
  ORDER_DELETED: "Deleted Order",
  PARTS_MASTER_BULK_UPLOAD: "Parts Master Bulk Upload",
};

const RecentActivityFeed = ({ entries }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-xl border border-[#e2e1eb]" data-testid="section-recent-activity">
      <div className="px-4 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-yamaha-700" />
          <h3 className="text-[13px] font-semibold text-[#1a1b22]">Recent User Activity</h3>
        </div>
        <button
          data-testid="view-full-audit-btn"
          onClick={() => navigate("/admin")}
          className="text-[11px] font-semibold text-yamaha-700 hover:underline"
        >
          View full audit log →
        </button>
      </div>
      {(!entries || entries.length === 0) ? (
        <div className="px-4 py-8 text-center text-[#747684] text-[12px]">
          No activity in the selected period.
        </div>
      ) : (
        <ol className="px-4 py-3 space-y-2.5">
          {entries.map((l) => (
            <li key={l.id} className="flex items-start gap-3 text-[12px]" data-testid={`recent-act-${l.id}`}>
              <span className="mt-1 w-2 h-2 rounded-full bg-yamaha-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div>
                  <span className="font-semibold text-[#1a1b22]">{l.actor || "—"}</span>
                  {l.actor_role ? <span className="text-[#747684]"> ({ROLE_LABELS[l.actor_role] || l.actor_role})</span> : null}
                  <span className="text-[#444653]"> · {ACTION_DISPLAY[l.action] || l.action}</span>
                  {l.target_label ? <span className="text-[#444653]"> · <span className="font-medium">{l.target_label}</span></span> : null}
                </div>
                <div className="text-[10.5px] text-[#747684] mt-0.5">
                  {fmtDateTime(l.at)}{l.details ? ` · ${l.details}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
